import { useCallback, useMemo, useRef } from 'react';
import type { ExpandedState } from '@tanstack/react-table';
import { parseAsString, useQueryStates } from 'nuqs';
import {
  Box,
  Button,
  DataTable,
  EmptyState,
  EmptyStateActions,
  EmptyStateFooter,
  EmptyStateTitle,
  Link,
  Panel,
  PanelInset,
} from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import { useAuth } from '@/services/auth';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { useAnalytics } from '@/services/analytics';
import { useRegisterBreadcrumbs } from '@/services/breadcrumbs';
import { useActivityLogEnvironment, useLogs } from '@/services/db/activity-log/queries';
import {
  activityLogTypeLabel,
  buildObjectActivityTypeOptions,
  createIdentifiedColumns,
  ExpandedRow,
  expandedRowIds,
  LogsFilterControls,
  LogsToolbar,
  newlyExpandedRowIds,
  specificActivityViewedProps,
  useLogsLoadNew,
  useObjectTypeById,
  type ActivityLogRow,
  type ActivityLogTypeTerms,
  type IdentifierConfig,
} from '@/features/activity-feed';
import { useObjectDetail, type ObjectDetailContextValue } from './context';

const filterParsers = {
  type: parseAsString.withDefault(''),
  name: parseAsString.withDefault(''),
  from: parseAsString.withDefault(''),
  to: parseAsString.withDefault(''),
};

const TYPE_TERMS: ActivityLogTypeTerms = {
  campaignSingular: 'Automation',
  personSingular: 'Person',
  personPlural: 'People',
};

/** What a resend from this tab reports, so the funnels count it apart from the workspace feed. */
const RESEND_SOURCE = 'Object Activity';

type EmptyStateProps = { onRetry: () => void };

function NoObjectActivity() {
  return (
    <EmptyState>
      <EmptyStateTitle icon="activity">No activity within the last 30 days</EmptyStateTitle>
      <EmptyStateFooter>
        <Link href="https://docs.customer.io/filtering-logs" variant="secondary" isExternal icon="help">
          Learn more about Activity
        </Link>
      </EmptyStateFooter>
    </EmptyState>
  );
}

/** A filter that matches nothing is not an object with no activity (Ember's filtered empty state). */
function NoMatchingActivity() {
  return (
    <EmptyState>
      <EmptyStateTitle icon="activity">No recent activity</EmptyStateTitle>
    </EmptyState>
  );
}

function ObjectActivityError({ onRetry }: EmptyStateProps) {
  return (
    <EmptyState>
      <EmptyStateTitle icon="activity">Couldn't load activity</EmptyStateTitle>
      <EmptyStateActions>
        <Button variant="secondary" onClick={onRetry}>
          Retry loading activity
        </Button>
      </EmptyStateActions>
    </EmptyState>
  );
}

/** Only a row the reader newly opens counts as viewing it (see `newlyExpandedRowIds`). */
function useExpansionTracking(rows: readonly ActivityLogRow[]) {
  const { track } = useAnalytics();
  const expandedRef = useRef<Record<string, boolean>>({});

  return useCallback(
    (next: ExpandedState) => {
      const expanded = expandedRowIds(next);
      const newlyExpanded = newlyExpandedRowIds(expandedRef.current, expanded);
      expandedRef.current = expanded;
      for (const id of newlyExpanded) {
        const row = rows.find((candidate) => candidate.log.id === id);
        if (row != null) {
          void track('logs', 'specificActivityViewed', specificActivityViewedProps('identified', row.log));
        }
      }
    },
    [rows, track],
  );
}

type ObjectActivityFiltersInput = Pick<ObjectDetailContextValue, 'objectType'>;

/**
 * The Activity type options, pinned to this object's type, and what the attribute pickers need
 * to know about that type.
 */
function useObjectActivityFilterOptions({ objectType }: ObjectActivityFiltersInput) {
  const { id, singular_name: singularName, sent_attributes: sentAttributes } = objectType;
  const sentRelAttributes = objectType.sent_rel_attributes;
  const activityTypeOptions = useMemo(
    () => buildObjectActivityTypeOptions(TYPE_TERMS, { id, singularName }),
    [id, singularName],
  );
  const nameFilterObjectType = useMemo(
    () => ({ id, sentAttributes, sentRelAttributes }),
    [id, sentAttributes, sentRelAttributes],
  );
  return { activityTypeOptions, nameFilterObjectType };
}

type ObjectActivityTableInput = {
  dataWorkspaceId: string;
  identifiers: IdentifierConfig;
  logs: ReturnType<typeof useLogs>;
  /** Whether any filter narrows the feed, which decides what an empty feed says. */
  isFiltered: boolean;
};

/** The table's empty slot: the retry on a failed read, else no matches or no activity at all. */
function emptyStateFor(isError: boolean, isFiltered: boolean, onRetry: () => void) {
  if (isError) return () => <ObjectActivityError onRetry={onRetry} />;
  return isFiltered ? NoMatchingActivity : NoObjectActivity;
}

/** The feed table's columns, expanded row, empty or error state, "load more", and expansion events. */
function useObjectActivityTable({ dataWorkspaceId, identifiers, logs, isFiltered }: ObjectActivityTableInput) {
  const { account } = useAuth();
  const { track } = useAnalytics();
  const objectTypeById = useObjectTypeById(dataWorkspaceId);
  const { rows, segmentById, refetch, hasMore, fetchMore, isFetchingMore, isError } = logs;
  const isBuilder = isBuilderPlan(account);

  const columns = useMemo(
    () =>
      createIdentifiedColumns({
        resolveTypeLabel: (row) => activityLogTypeLabel(row.log, TYPE_TERMS),
        workspaceId: dataWorkspaceId,
        personSingular: TYPE_TERMS.personSingular,
        objectTypeById,
        isBuilder,
        identifiers,
      }),
    [dataWorkspaceId, objectTypeById, isBuilder, identifiers],
  );

  const expandedRowComponent = useCallback(
    ({ row }: { row: { original: ActivityLogRow } }) => (
      <ExpandedRow
        row={row.original}
        workspaceId={dataWorkspaceId}
        dataWorkspaceId={dataWorkspaceId}
        campaignSingular={TYPE_TERMS.campaignSingular}
        isAnonymous={false}
        identifiers={identifiers}
        segmentById={segmentById}
        onResent={() => void refetch()}
        resend={{ source: RESEND_SOURCE }}
      />
    ),
    [dataWorkspaceId, identifiers, segmentById, refetch],
  );

  const emptyStateComponent = useMemo(
    () => emptyStateFor(isError, isFiltered, () => void refetch()),
    [isError, isFiltered, refetch],
  );

  const loadMore = useMemo(
    () => ({
      canLoad: hasMore,
      onClick: () => {
        void track('logs', 'moreLogsLoaded', { 'Activity Tab': 'Identified' });
        void fetchMore();
      },
      isLoading: isFetchingMore,
      label: 'Load more',
    }),
    [hasMore, fetchMore, isFetchingMore, track],
  );

  return {
    columns,
    expandedRowComponent,
    emptyStateComponent,
    loadMore,
    trackRowsExpanded: useExpansionTracking(rows),
  };
}

/**
 * One object's own activity: the workspace activity feed narrowed to this object, with the
 * Activity type filter offering only the object and relationship-attribute types, pinned to this
 * object's type (Ember's `object.activity` route).
 */
export function ObjectActivityTab() {
  const { record, objectType, workspaceId, hrefs } = useObjectDetail();
  const [filters, setFilters] = useQueryStates(filterParsers, { history: 'replace' });
  const dataWorkspaceId = String(workspaceId);
  const cioObjectId = record.identifiers.cio_object_id;

  const activityHref = hrefs.tabs.activity;
  useRegisterBreadcrumbs(
    useMemo(() => [{ id: 'object.activity', text: 'Profile activity', href: activityHref }], [activityHref]),
  );

  const logs = useLogs({ environmentId: dataWorkspaceId, tab: 'identified', filters, cioObjectId });
  const { loadNew, status: newCheckStatus } = useLogsLoadNew({
    tab: 'identified',
    filters,
    dataWorkspaceId,
    cioObjectId,
    logs,
  });
  const { sentEvents, sentAttributes, identifiers } = useActivityLogEnvironment(dataWorkspaceId);
  const { activityTypeOptions, nameFilterObjectType } = useObjectActivityFilterOptions({ objectType });
  const isFiltered = Object.values(filters).some((value) => !isEmptyString(value));
  const table = useObjectActivityTable({ dataWorkspaceId, identifiers, logs, isFiltered });
  const { rows } = logs;

  return (
    <Box data-test-object-activity>
      <Box mt="150" mb="100">
        <LogsToolbar newCheckStatus={newCheckStatus}>
          <LogsFilterControls
            environmentId={dataWorkspaceId}
            filters={filters}
            setFilters={(values) => void setFilters(values)}
            activityTypeOptions={activityTypeOptions}
            sentEvents={sentEvents}
            sentAttributes={sentAttributes}
            objectType={nameFilterObjectType}
          />
        </LogsToolbar>
      </Box>
      <Panel>
        <PanelInset>
          <DataTable
            caption="Activity"
            data={rows}
            columns={table.columns}
            getRowId={(row) => row.log.id}
            isLoading={logs.isLoading || (logs.isFetching && rows.length === 0)}
            emptyStateComponent={table.emptyStateComponent}
            // Pluma draws the "load new" row only when its empty state is off.
            withEmptyState={loadNew.canLoad !== true}
            layout="fixed"
            withSorting={false}
            withRowExpanding
            getRowCanExpand={() => true}
            expandedRowComponent={table.expandedRowComponent}
            onRowExpansionChange={table.trackRowsExpanded}
            loadNew={loadNew}
            loadMore={table.loadMore}
          />
        </PanelInset>
      </Panel>
    </Box>
  );
}
